import { shouldFlushCompareAtPrice } from "@intelligems/ig-utils/price";
import { createSlice } from "@reduxjs/toolkit";
import {
  AgProduct,
  AgProductsSliceState,
} from "@src/store/slices/agProducts/types";
import { experienceProductVariantToAgProduct } from "@src/store/slices/agProducts/util/experienceProductVariantToAgProduct";
import { parsePrice } from "@src/store/slices/agProducts/util/parsePrice";
import { initExperienceProducts } from "@src/store/slices/experienceProducts";

const initialState: AgProductsSliceState = {
  loading: false,
  selectedAgGroups: {},
  template: { building: false },
};

const slice = createSlice({
  name: "agProducts",
  initialState,
  reducers: (create) => ({
    resetAgProducts: create.reducer(() => {
      return initialState;
    }),
    setAgProductsLoading: create.reducer<boolean>((state, action) => {
      state.loading = action.payload;
    }),
    initAgProducts: create.reducer<AgProduct[]>((state, action) => {
      state.agProducts = action.payload;
      state.storedAgProducts = state.agProducts;
    }),
    setAgProducts: create.reducer<AgProduct[]>((state, action) => {
      state.agProducts = action.payload.map((p) => {
        for (const [id, price] of Object.entries(p.variationPrices)) {
          if (
            shouldFlushCompareAtPrice(price.ogPrice, price.ogCompareAtPrice)
          ) {
            p.variationPrices[id].compareAtPrice = null;
            p.variationPrices[id].ogCompareAtPrice = null;
          }
        }
        return p;
      });
    }),
    setAgProduct: create.reducer<AgProduct>((state, action) => {
      if (!state.agProducts) {
        return;
      }

      const { productId, variantId } = action.payload;
      const index = state.agProducts.findIndex(
        (p) => p.productId === productId && p.variantId === variantId
      );
      if (index > -1) {
        state.agProducts[index] = action.payload;
      }
    }),
    selectAgGroup: create.reducer<string>((state, action) => {
      state.selectedAgGroups[action.payload] = true;
    }),
    deselectAgGroup: create.reducer<string>((state, action) => {
      delete state.selectedAgGroups[action.payload];
    }),
    deleteAgGroup: create.reducer<string>((state, action) => {
      state.agProducts = state.agProducts?.filter(
        (p) => p.productId != action.payload
      );
    }),
    startBuildAgProductTemplate: create.reducer<void>((state) => {
      state.template.building = true;
    }),
    finishBuildAgProductTemplate: create.reducer<string | undefined>(
      (state, action) => {
        state.template.building = false;
        state.template.location = action.payload;
      }
    ),
    resetAgProductTemplate: create.reducer((state) => {
      state.template.building = false;
      delete state.template.location;
    }),
    processSavedAgProducts: create.reducer((state) => {
      state.storedAgProducts = state.agProducts;
    }),
  }),
  selectors: {
    getAgProducts: (state) => state.agProducts,
    getSelectedAgGroups: (state) => state.selectedAgGroups,
    areAgProductsLoading: (state) => state.loading,
    isAgProductTemplateBuilding: (state) => state.template.building,
    getAgProductTemplateLocation: (state) => state.template.location,
  },
  extraReducers: (builder) => {
    builder.addCase(initExperienceProducts, (state, action) => {
      const agProducts: AgProduct[] = [];
      for (const product of action.payload.experienceProducts) {
        for (const variant of product.variants) {
          agProducts.push(
            experienceProductVariantToAgProduct(product, variant)
          );
        }
      }
      state.agProducts = agProducts;
      state.storedAgProducts = agProducts;
    });
  },
});

export const agProductsReducer = slice.reducer;

export const {
  resetAgProducts,
  setAgProductsLoading,
  initAgProducts,
  setAgProducts,
  setAgProduct,
  selectAgGroup,
  deselectAgGroup,
  deleteAgGroup,
  startBuildAgProductTemplate,
  finishBuildAgProductTemplate,
  resetAgProductTemplate,
  processSavedAgProducts,
} = slice.actions;

export const {
  getAgProducts,
  areAgProductsLoading,
  getSelectedAgGroups,
  isAgProductTemplateBuilding,
  getAgProductTemplateLocation,
} = slice.selectors;
